import { ReactNode, useState } from 'react';
import classNames from 'classnames';

interface Person {
  name: string;
  photo: string;
  about: ReactNode;
  position?: ReactNode;
  quote: ReactNode;
}

interface Props {
  color: 'light' | 'dark' | 'white';
  subtitle: ReactNode;
  title: ReactNode;
  perex: ReactNode;
  children: ReactNode;
  person: Person;
}

function Topic({ color, title, subtitle, perex, children, person }: Props) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={classNames('py-16 md:py-24', {
        'bg-blue-50': color === 'light',
        'bg-blue-900': color === 'dark',
      })}
    >
      <div className="max-w-5xl px-4 mx-auto">
        <div className="text-center mb-8">
          <p
            className={classNames('text-base font-semibold tracking-wide uppercase', {
              'text-red-700': color === 'light' || color === 'white',
              'text-red-400': color === 'dark',
            })}
          >
            {subtitle}
          </p>
          <h2
            className={classNames('text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl', {
              'text-blue-900': color === 'light' || color === 'white',
              'text-white': color === 'dark',
            })}
          >
            {title}
          </h2>
        </div>
        <div
          className={classNames('mt-4 flex flex-col gap-4 md:text-xl', {
            'text-gray-500': color === 'light' || color === 'white',
            'text-neutral-300': color === 'dark',
          })}
        >
          {!expanded && <>{perex}</>}
          {expanded && <>{children}</>}
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-sm font-medium rounded-full text-blue-900 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Sbalit obsah' : 'Zjistit více'}
            </button>
          </div>
        </div>
        <div
          className={classNames('rounded-lg p-6 mt-8 md:mt-16', {
            'bg-white shadow': color === 'light',
            'bg-white shadow-lg': color === 'dark',
            'bg-white shadow border border-neutral-100': color === 'white',
          })}
        >
          <div className="flex flex-col md:flex-row items-center gap-6 border-b border-neutral-100 mb-4 pb-4">
            <img
              src={person.photo}
              alt={person.name}
              className="w-24 h-24 rounded-full border border-neutral-100 object-cover"
            />
            <div className="text-center md:text-left">
              <h3 className="text-xl font-semibold text-blue-900 uppercase">{person.name}</h3>
              <p className="text-md text-neutral-600 max-w-4xl">{person.about}</p>
              {person.position && <strong className="block pt-2 md:pt-0">{person.position}</strong>}
            </div>
          </div>
          <p>
            <span className="text-lg text-neutral-600">„</span>
            {person.quote}
            <span className="text-lg text-neutral-600">“</span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Topic;
